import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PolicyProvider } from './contexts/PolicyContext';
import AuthPage from './components/AuthPage';
import AdminDashboard from './components/AdminDashboard';
import MemberDashboard from './components/MemberDashboard';
import PatientDashboard from './components/PatientDashboard';
import PatientRegister from './components/auth/PatientRegister';
import ClinicianRegister from './components/auth/ClinicianRegister';
import RegisterLanding from './components/auth/RegisterLanding';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import ClinicianDirectory from './components/public/ClinicianDirectory';
import ClinicianProfilePage from './components/public/ClinicianProfile';
import PrivacyPolicy from './components/public/PrivacyPolicy';
import TermsOfService from './components/public/TermsOfService';
import ClientsPage from './components/public/ClientsPage';
import CheckIn from './components/public/CheckIn';
import Kiosk from './components/public/Kiosk';
import DisplayBoard from './components/public/DisplayBoard';
import './App.css';

// Loading spinner component
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-clinic-surface-sunken">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-primary mx-auto mb-4"></div>
      <p className="text-clinic-muted">Loading...</p>
    </div>
  </div>
);

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles: string[] }> = ({
  children,
  allowedRoles,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user?.role || '')) {
    // Redirect to appropriate dashboard based on role
    const dashboardRoute = user?.role === 'admin' ? '/admin' : user?.role === 'member' ? '/member' : '/patient';
    return <Navigate to={dashboardRoute} replace />;
  }

  return <>{children}</>;
};

// Member-specific protected route with billing gate
// Clinicians MUST complete billing setup before accessing the dashboard
const MemberProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'member') {
    // Non-members go to their appropriate dashboard
    const dashboardRoute = user?.role === 'admin' ? '/admin' : '/patient';
    return <Navigate to={dashboardRoute} replace />;
  }

  return <>{children}</>;
};

// Home redirect based on auth status
const HomeRedirect: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const dashboardRoute = user?.role === 'admin' ? '/admin' : user?.role === 'member' ? '/member' : '/patient';
  return <Navigate to={dashboardRoute} replace />;
};

// Login page - redirects if already authenticated
const LoginRoute: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    // Honor redirect param if it's a safe internal path
    if (redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//')) {
      // Allow public routes (e.g., /clinicians) for any role
      const isPublicRoute = redirectTo.startsWith('/clinicians');
      // Allow role-specific routes only for matching roles
      const isRoleRoute = (
        (user?.role === 'patient' && redirectTo.startsWith('/patient')) ||
        (user?.role === 'member' && redirectTo.startsWith('/member')) ||
        (user?.role === 'admin' && redirectTo.startsWith('/admin'))
      );
      if (isPublicRoute || isRoleRoute) {
        return <Navigate to={redirectTo} replace />;
      }
    }
    const dashboardRoute = user?.role === 'admin' ? '/admin' : user?.role === 'member' ? '/member' : '/patient';
    return <Navigate to={dashboardRoute} replace />;
  }

  return <AuthPage />;
};

const MainApp: React.FC = () => {
  return (
    <Routes>
      {/* Home redirect */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Public routes - no auth required */}
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<RegisterLanding />} />
      <Route path="/register/patient" element={<PatientRegister />} />
      <Route path="/register/clinician" element={<ClinicianRegister />} />
      <Route path="/clinicians" element={<ClinicianDirectory />} />
      <Route path="/clients" element={<ClientsPage />} />
      <Route path="/clinicians/:slug" element={<ClinicianProfilePage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/checkin" element={<CheckIn />} />
      <Route path="/kiosk" element={<Kiosk />} />
      <Route path="/display/calendar" element={<DisplayBoard />} />

      {/* Protected routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      {/* Member routes */}
      <Route
        path="/member/*"
        element={
          <MemberProtectedRoute>
            <MemberDashboard />
          </MemberProtectedRoute>
        }
      />
      <Route
        path="/patient/*"
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientDashboard />
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <PolicyProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <MainApp />
        <Toaster
          position="top-right"
          containerStyle={{ top: 70 }}
          toastOptions={{
            duration: 3000,
            style: {
              background: '#383934', // clinic-dark
              color: '#FFF4D7',      // clinic-cream
              fontFamily: 'Montserrat, sans-serif',
              borderRadius: '8px',
            },
            success: {
              style: {
                background: '#687666', // clinic-primary (sage green)
              },
              iconTheme: {
                primary: '#FFF4D7',
                secondary: '#687666',
              },
            },
            error: {
              style: {
                background: '#895A4C', // clinic-accent (terracotta)
              },
              iconTheme: {
                primary: '#FFF4D7',
                secondary: '#895A4C',
              },
            },
          }}
        />
      </Router>
      </PolicyProvider>
    </AuthProvider>
  );
}

export default App;